import { useMemo, useState } from 'react'
import { AlertTriangle, CircleDollarSign, Clock, Mail } from 'lucide-react'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { EmailServiceAlert } from '@/features/email-service'
import { useMembersStore } from '@/features/members/store/membersStore'
import { PendingDonationForm } from '@/features/donations/components/PendingDonationForm'
import { useAuthStore } from '@/features/auth/store/authStore'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePermissions } from '@/utils/permissions'
import { useExpensesStore } from '@/features/expenses/store/expensesStore'
import { ExpenseDetails } from '@/features/expenses/components/ExpenseDetails'
import { CashNotDeposit } from '@/features/home/components/CashNotDeposit'
import { DonationsWithoutSentReceipts } from '@/features/home/components/DonationsWithoutSentReceipts'
import { PendingDonations } from '@/features/home/components/PendingDonations'
import { PendingExpenses } from '@/features/home/components/PendingExpenses'

export default function Home() {
  const user = useAuthStore((state) => state.user)
  const permissions = usePermissions()
  const donations = useDonationsStore((s) => s.donations)
  const expenses = useExpensesStore((s) => s.expenses)
  const members = useMembersStore((s) => s.members)

  const [open, setOpen] = useState({ donation: false, expense: false })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('pending')

  // Filter donations by status and access
  const pending = useMemo(
    () => donations.filter((d) => d.status === 'pending'),
    [donations],
  )

  const unsentReceipts = useMemo(
    () =>
      donations.filter(
        (d) =>
          !d.sentToMember && d.status === 'verified' && !d.isRegistrationFee,
      ),
    [donations],
  )

  const notDepositedCashDonations = useMemo(
    () => donations.filter((d) => d.method === 'cash' && !d.deposited),
    [donations],
  )

  const pendingExpenses = useMemo(
    () =>
      expenses.filter((e) => {
        if (e.status !== 'pending') return false

        // Determine current approval stage based on what's been approved
        const localPastorApproved = !!e.localPastor?.approvalDate
        const adminPastorApproved = !!e.adminPastor?.approvalDate
        const adminTreasurerApproved = !!e.adminTreasurer?.approvalDate

        // First stage: waiting for local pastor approval
        if (!localPastorApproved) {
          return user?._id === e.localPastor?.userId
        }

        // Second stage: waiting for admin pastor approval
        if (!adminPastorApproved) {
          return user?._id === e.adminPastor?.userId
        }

        // Third stage: waiting for admin treasurer approval
        if (!adminTreasurerApproved) {
          return user?._id === e.adminTreasurer?.userId
        }

        return false
      }),
    [expenses, user],
  )

  const handleDonationVerified = (verifiedId: string) => {
    const idx = pending.findIndex((d) => d._id === verifiedId)

    if (
      idx === -1 ||
      pending.length === 0 ||
      idx === pending.length - 1 ||
      idx + 1 >= pending.length
    ) {
      setSelectedId(null)
      setOpen({ ...open, donation: false })
      return
    }

    const next =
      pending[idx + 1] || pending.find((d) => d._id !== verifiedId) || null

    setSelectedId(next._id)
    setOpen({ ...open, donation: true })
  }

  const getDonorName = (donorId: string | null) => {
    if (!donorId) return 'Unassigned Donor'
    const member = members.find((m) => m._id === donorId)
    return member?.name || 'Unknown'
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Dashboard Header */}
      <h1 className="text-xl font-semibold mb-6">Welcome, {user?.name}</h1>

      <ScrollArea className="flex-1">
        <div className="space-y-4 h-full flex flex-col lg:block">
          {/* Email Service Status Alert */}
          <EmailServiceAlert />

          {/* Desktop Layout - Show both sections separately */}
          <div className="hidden lg:block space-y-4">
            <PendingDonations
              donations={pending}
              donorNameResolver={getDonorName}
              onVerifyClick={(donationId) => {
                setSelectedId(donationId)
                setOpen({ ...open, donation: true })
              }}
            />

            <DonationsWithoutSentReceipts
              donations={unsentReceipts}
              donorNameResolver={getDonorName}
            />

            <CashNotDeposit
              donations={notDepositedCashDonations}
              donorNameResolver={getDonorName}
            />

            {permissions.canViewPendingExpenses() && (
              <PendingExpenses
                expenses={pendingExpenses}
                onOpenDetails={(expenseId) => {
                  setSelectedId(expenseId)
                  setOpen({ ...open, expense: true })
                }}
              />
            )}
          </div>

          {/* Mobile Layout - Select */}
          <div className="lg:hidden flex-1 flex flex-col">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="bg-white rounded-lg border flex-1 flex flex-col"
            >
              <div className="px-6 py-4 border-b border-gray-200 shrink-0">
                <Select value={activeTab} onValueChange={setActiveTab}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Pending ({pending.length})
                      </div>
                    </SelectItem>
                    <SelectItem value="unsent">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Unsent ({unsentReceipts.length})
                      </div>
                    </SelectItem>
                    <SelectItem value="cash">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Cash ({notDepositedCashDonations.length})
                      </div>
                    </SelectItem>
                    {permissions.canViewPendingExpenses() && (
                      <SelectItem value="expenses">
                        <div className="flex items-center gap-2">
                          <CircleDollarSign className="h-4 w-4" />
                          Expenses ({pendingExpenses.length})
                        </div>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <TabsContent
                value="pending"
                className="flex-1 flex flex-col p-6 m-0"
              >
                <PendingDonations
                  donations={pending}
                  donorNameResolver={getDonorName}
                  onVerifyClick={(donationId) => {
                    setSelectedId(donationId)
                    setOpen({ ...open, donation: true })
                  }}
                />
              </TabsContent>

              <TabsContent
                value="unsent"
                className="flex-1 flex flex-col p-6 m-0"
              >
                <DonationsWithoutSentReceipts
                  donations={unsentReceipts}
                  donorNameResolver={getDonorName}
                />
              </TabsContent>

              <TabsContent
                value="cash"
                className="flex-1 flex flex-col p-6 m-0"
              >
                <CashNotDeposit
                  donations={notDepositedCashDonations}
                  donorNameResolver={getDonorName}
                />
              </TabsContent>
              <TabsContent
                value="expenses"
                className="flex-1 flex flex-col p-6 m-0"
              >
                <PendingExpenses
                  expenses={pendingExpenses}
                  onOpenDetails={(expenseId) => {
                    setSelectedId(expenseId)
                    setOpen({ ...open, expense: true })
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Pending Donation Form Dialog */}
          <PendingDonationForm
            id={selectedId}
            open={open.donation}
            onClose={() => {
              setOpen({ ...open, donation: false })
              setSelectedId(null)
            }}
            onVerified={handleDonationVerified}
          />

          <ExpenseDetails
            id={selectedId}
            open={open.expense}
            onClose={() => {
              setOpen({ ...open, expense: false })
              setSelectedId(null)
            }}
          />
        </div>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </div>
  )
}
